import { useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { Image } from 'foundations-components/transitional/components/Image';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { LearningContentCardsSkeleton } from 'academy-common-lib/components/Skeletons/LearningContentCardsSkeleton';
import { ACContentCardSmall } from 'academy-common-lib/components/card/ACContentCardSmall';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { useEffectEvent } from 'academy-common-lib/hooks/useEffectEvent';
import { ThumbnailTypeMap } from 'academy-common-lib/features/cards/constants';
import { useLearningContentCardTo, useThumbnailUrl } from 'academy-common-lib/features/learningContentCard/cardsHooks';
import { THUMBNAIL_SIZES } from 'academy-common-lib/features/learningContentCard/thumbnailPresets';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import getLang from 'academy-common-lib/utils/getLang';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
// @ts-ignore Could not find a declaration file for module
import playlistAvatarUrl from 'bender-url!academy-ai-assistant-lib/img/playlist-avatar.png';
import { useAcademyChatActions } from '../../context/AcademyChatActionsContext';
import { useCreateSuggestedPlaylist } from '../../hooks/useCreateSuggestedPlaylist';
import { askAboutUpcomingContent } from '../../utils/askAboutUpcomingContent';
import { getPlaylistDraftContext, normalizePlaylistItemType } from './playlistSuggestionUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ITEM_TYPE_TO_CONTENT_TYPE = {
  COURSE: 'TRACK',
  LESSON: 'LESSON',
  SHORT_VIDEO: 'SHORT_FORM_CONTENT'
};
const getContentDuration = model => {
  if ('duration' in model && model.duration) {
    return model.duration;
  }
  if ('itemsCount' in model && model.itemsCount) {
    return model.itemsCount;
  }
  return 0;
};
const PlaylistSuggestionItem = ({
  model,
  contentType,
  onItemClick
}) => {
  const {
    name,
    previewThumbnailSquareUrl
  } = model;
  const {
    cardRef,
    imageSrc,
    onThumbnailLoad
  } = useThumbnailUrl({
    thumbnailUrl: previewThumbnailSquareUrl,
    size: THUMBNAIL_SIZES.CARD_THUMBNAIL_SMALL
  });
  const {
    fullUrl
  } = useLearningContentCardTo({
    item: model,
    overviewType: 'academy-assistant'
  });
  const thumbnailContentType = 'certification' in model && model.certification ? 'CERTIFICATION' : ThumbnailTypeMap[contentType];
  const length = getContentDuration(model);
  return /*#__PURE__*/_jsx(ACContentCardSmall, {
    bordered: false,
    cardRef: cardRef,
    contentHref: fullUrl,
    contentType: thumbnailContentType,
    imageSrc: imageSrc,
    length: length,
    lengthType: "time",
    linkMode: "react-router",
    name: name,
    onClick: onItemClick,
    onThumbnailLoad: onThumbnailLoad,
    overviewType: "academy-assistant",
    size: 60
  });
};
export const PlaylistSuggestionCard = ({
  section,
  message
}) => {
  var _playlistDraft$name, _playlistDraft$descri, _playlistDraft$name2;
  const {
    onContentCardClick,
    onContentCardImpression,
    onPlaylistStartClick,
    onPlaylistManageClick,
    onPlaylistSuggested
  } = useAcademyChatActions();
  const {
    isMobile
  } = useMediaQuery();
  const cardPadding = isMobile ? '400' : '600';
  const [isOpen, setIsOpen] = useState(false);
  const trackedImpressionsRef = useRef(new Set());
  const items = useMemo(() => {
    var _section$items;
    return (_section$items = section.items) !== null && _section$items !== void 0 ? _section$items : [];
  }, [section.items]);
  const playlistDraft = getPlaylistDraftContext(message, section);
  const validItems = useMemo(() => items.map(item => {
    const itemType = normalizePlaylistItemType(item.type);
    if (!itemType) return null;
    const contentType = ITEM_TYPE_TO_CONTENT_TYPE[itemType];
    if (!contentType) return null;
    return {
      playlistItem: Object.assign({}, item, {
        type: itemType
      }),
      contentType
    };
  }).filter(item => item !== null), [items]);
  const learningContentItems = useMemo(() => validItems.map(({
    playlistItem,
    contentType
  }) => {
    var _playlistItem$languag;
    return {
      id: playlistItem.itemId,
      type: contentType,
      language: playlistItem.type === 'COURSE' || playlistItem.type === 'LESSON' ? (_playlistItem$languag = playlistItem.language) === null || _playlistItem$languag === void 0 ? void 0 : _playlistItem$languag.toLowerCase() : undefined
    };
  }), [validItems]);
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: learningContentItems,
    skip: !learningContentItems.length
  });
  const renderableItems = useMemo(() => {
    const validItemsById = new Map(validItems.map(validItem => [validItem.playlistItem.itemId, validItem]));
    return (cardModels !== null && cardModels !== void 0 ? cardModels : []).map(model => {
      if (!(model !== null && model !== void 0 && model.id)) return null;
      const validItem = validItemsById.get(model.id);
      return validItem ? {
        model,
        validItem
      } : null;
    }).filter(item => item !== null);
  }, [cardModels, validItems]);
  const playlistItemRequests = useMemo(() => renderableItems.map(({
    validItem
  }) => {
    var _playlistItem$languag2;
    const {
      playlistItem,
      contentType
    } = validItem;
    const language = convertToSupportedLanguage((_playlistItem$languag2 = playlistItem.language) !== null && _playlistItem$languag2 !== void 0 ? _playlistItem$languag2 : getLang());
    return {
      itemId: playlistItem.itemId,
      type: contentType,
      language
    };
  }), [renderableItems]);
  const {
    startPlaylist,
    managePlaylist,
    isCreatingPlaylist
  } = useCreateSuggestedPlaylist({
    name: (_playlistDraft$name = playlistDraft === null || playlistDraft === void 0 ? void 0 : playlistDraft.name) !== null && _playlistDraft$name !== void 0 ? _playlistDraft$name : I18n.text('academy-ai-assistant.playlist.title'),
    description: (_playlistDraft$descri = playlistDraft === null || playlistDraft === void 0 ? void 0 : playlistDraft.description) !== null && _playlistDraft$descri !== void 0 ? _playlistDraft$descri : '',
    items: playlistItemRequests
  });
  const isPlaylistShown = !cardModelsLoading && !cardModelsError && renderableItems.length > 0;
  const trackPlaylistSuggested = useEffectEvent(() => {
    onPlaylistSuggested();
  });
  useEffect(() => {
    if (isPlaylistShown) {
      trackPlaylistSuggested();
    }
  }, [isPlaylistShown]);
  const trackItemImpressions = useEffectEvent(() => {
    for (const {
      validItem
    } of renderableItems) {
      const {
        playlistItem,
        contentType
      } = validItem;
      if (trackedImpressionsRef.current.has(playlistItem.itemId)) continue;
      trackedImpressionsRef.current.add(playlistItem.itemId);
      onContentCardImpression({
        contentId: playlistItem.itemId,
        contentType,
        appearsIn: 'playlist-card'
      });
    }
  });
  useEffect(() => {
    trackItemImpressions();
  }, [renderableItems]);
  if (!items.length) {
    return null;
  }
  if (cardModelsLoading) {
    return /*#__PURE__*/_jsx(CardWrapper, {
      compact: true,
      marginBlock: "400",
      blockSize: "auto",
      children: /*#__PURE__*/_jsx(View, {
        padding: "600",
        children: /*#__PURE__*/_jsx(LearningContentCardsSkeleton, {
          skeletonCardSize: "small",
          use: "on-light",
          maxSkeletonListLength: 1
        })
      })
    });
  }
  if (cardModelsError || !renderableItems.length) {
    return null;
  }
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    marginBlock: "400",
    blockSize: "auto",
    children: [/*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      borderBlockEndColor: "border-core-subtle",
      borderBlockEndStyle: "solid",
      borderBlockEndWidth: "100",
      gap: "300",
      padding: cardPadding,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: isMobile ? undefined : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: "wrap",
        gap: "300",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexGrow: 1,
          gap: "200",
          minInlineSize: "0",
          children: [/*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-magenta-subtle-alt",
            blockSize: "32px",
            borderRadius: "200",
            flexShrink: 0,
            inlineSize: "32px",
            overflow: "hidden",
            children: /*#__PURE__*/_jsx(Image, {
              alt: "",
              blockSize: "32px",
              inlineSize: "32px",
              objectFit: "cover",
              responsive: false,
              src: playlistAvatarUrl
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "100",
            minInlineSize: "0",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "strong",
              overflowWrap: "break-word",
              typography: "heading-200",
              children: (_playlistDraft$name2 = playlistDraft === null || playlistDraft === void 0 ? void 0 : playlistDraft.name) !== null && _playlistDraft$name2 !== void 0 ? _playlistDraft$name2 : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academy-ai-assistant.playlist.title"
              })
            }), /*#__PURE__*/_jsx(Text, {
              color: "text-core-subtle",
              typography: "body-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academy-ai-assistant.playlist.itemCount",
                options: {
                  count: items.length
                }
              })
            })]
          })]
        }), /*#__PURE__*/_jsx(LoadingButton, {
          block: isMobile,
          flexShrink: 0,
          loading: isCreatingPlaylist,
          onClick: () => {
            onPlaylistStartClick();
            void startPlaylist();
          },
          preventClicksOnLoading: true,
          responsive: false,
          size: "extra-small",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.playlist.startButton"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(View, {
      padding: isMobile ? '300' : '400',
      children: /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        size: "extra-small",
        onClick: () => setIsOpen(prev => !prev),
        "aria-expanded": isOpen,
        "aria-label": isOpen ? I18n.text('academy-ai-assistant.playlist.collapse') : I18n.text('academy-ai-assistant.playlist.expand'),
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "300",
          children: [isOpen ? /*#__PURE__*/_jsx(DownIcon, {
            color: "icon-interactive-default",
            size: "100"
          }) : /*#__PURE__*/_jsx(RightIcon, {
            color: "icon-interactive-default",
            size: "100"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: isOpen ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academy-ai-assistant.playlist.hideItems"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academy-ai-assistant.playlist.showItems"
            })
          })]
        })
      })
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: isOpen,
      children: /*#__PURE__*/_jsxs(View, {
        paddingBlockEnd: cardPadding,
        paddingInline: cardPadding,
        children: [/*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          gap: "400",
          children: renderableItems.map(({
            model,
            validItem
          }) => {
            const {
              playlistItem,
              contentType
            } = validItem;
            return /*#__PURE__*/_jsx(PlaylistSuggestionItem, {
              contentType: contentType,
              model: model,
              onItemClick: () => {
                onContentCardClick({
                  contentId: playlistItem.itemId,
                  contentType,
                  appearsIn: 'playlist-card'
                });
                if (contentType === 'LESSON' || contentType === 'SHORT_FORM_CONTENT') {
                  askAboutUpcomingContent(I18n.text('academy-ai-assistant.actions.videoContentAutoPrompt'), {
                    mode: 'sidebar',
                    source: 'playlist-card'
                  });
                }
              }
            }, playlistItem.itemId);
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(LoadingButton, {
            loading: isCreatingPlaylist,
            onClick: () => {
              onPlaylistManageClick();
              void managePlaylist();
            },
            preventClicksOnLoading: true,
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academy-ai-assistant.playlist.managePlaylist"
            })
          })
        })]
      })
    })]
  });
};